import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { useDeleteWorkspaceMember } from "@/queries";
import { authHeader } from "@/utils/headers";
import { Workspace } from "@/types/workspaces";

interface UseRemoveMemberFromWorkspaceParams {
  targetUserId: number;
  onSuccess?: (workspace: Workspace) => void;
}

export const useRemoveMemberFromWorkspace = ({
  targetUserId,
  onSuccess,
}: UseRemoveMemberFromWorkspaceParams) => {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { mutateAsync: deleteWorkspaceMember } = useDeleteWorkspaceMember(auth?.userToken ?? "");

  const removeMemberFromWorkspace = useCallback(
    async (workspace: Workspace) => {
      const res = await fetch(ENDPOINTS.get_workspace_members(workspace.id.toString()), {
        method: "GET",
        headers: authHeader(auth?.userToken ?? ""),
      });

      if (!res.ok) {
        throw new Error("Failed to fetch workspace members");
      }

      const data = (await res.json()) as { members?: { id: number; user_id: number }[] };
      const member = data.members?.find(m => m.user_id === targetUserId);

      if (!member?.id) {
        throw new Error("Member not found in workspace");
      }

      await deleteWorkspaceMember(member.id);
      queryClient.invalidateQueries([ENDPOINTS.organization_members]);
      onSuccess?.(workspace);
    },
    [auth?.userToken, targetUserId, deleteWorkspaceMember, queryClient, onSuccess],
  );

  return { removeMemberFromWorkspace };
};
